import _ from 'lodash';
import * as tpa from '#packages/tpa';
import constants from '#packages/constants';
import addPanelDataConsts from '#packages/addPanelDataConsts';
import * as stateManagement from '#packages/stateManagement';
import type { EditorAPI } from '#packages/editorAPI';
import * as util from '#packages/util';
import experiment from 'experiment';

const { isRefComponentSupported } = stateManagement.platform.selectors;

const WIX_BOOKINGS_APP_DEF_ID = '13d21c63-b5ec-5912-8397-c3a5ddb27a97';
const WIX_STORE_APP_DEF_ID = util.tpaUtils.getStoresAppDefId();
const RELEVANT_POST_REGS = [
  'business',
  'photography',
  'design',
  'events',
  'beauty-wellness',
  'other',
  'all',
  'fitness',
  null,
];

const SETUP_STEPS = {
  createService: 'create-service',
  editService: 'edit-service',
  workingHours: 'working-hours',
  email: 'email',
  payment: 'payment',
  done: 'done',
  undefined: 'undefined',
};

const BOOKINGS_BO_PAGES = {
  offeringList: 'scheduler/owner/offerings',
  calendar: 'scheduler/owner/calendar',
  newForm: 'scheduler/owner/offerings/wizard',
  settings: 'settings',
  availability: 'scheduler/owner/bookings-options/calendar-settings',
  email: 'settings-email',
  payment: 'payments',
};

function getAppData(editorAPI: EditorAPI) {
  const appData = editorAPI.dsRead.tpa.app.getDataByAppDefId(
    WIX_BOOKINGS_APP_DEF_ID,
  );

  return appData ? appData : {};
}

function parseInstance(instance: AnyFixMe) {
  const base64Instance = instance.split('.')[1];
  return base64Instance && JSON.parse(window.atob(base64Instance));
}

const params = {
  WIX_BOOKINGS_APP_DEF_ID,
  WIX_STORE_APP_DEF_ID,
  RELEVANT_POST_REGS,
  SETUP_STEPS,
  BOOKINGS_BO_PAGES,
};

const superAppData = {
  superAppId: 'wixBookings',
  superAppPanelName: constants.SUPER_APPS.BOOKINGS_PANEL_NAME,
  superAppButtonLabel: 'SiteApp_WixBookings_Button_Hover_Caption',
  superAppPanelClassName: 'wix-bookings-panel',
  superAppSymbolName: util.workspace.isNewWorkspaceEnabled()
    ? 'leftBarButtonBookings'
    : 'leftBarWixBookingsPanel',
};

const featureToggles = {
  isBookingsTimetableEnabled: (editorAPI: EditorAPI) =>
    isRefComponentSupported(editorAPI),
  isBookingsStaffListWidgetEnabled: (editorAPI: EditorAPI) =>
    isRefComponentSupported(editorAPI),
};

const editorApiReliantMethods = {
  getMetaSiteId: (editorAPI: EditorAPI) =>
    editorAPI.dsRead.generalInfo.getMetaSiteId(),
  getBiToken: (editorAPI: EditorAPI) =>
    // @ts-expect-error
    parseInstance(getAppData(editorAPI).instance)?.biToken,
  // @ts-expect-error
  getInstanceId: (editorAPI: EditorAPI) => getAppData(editorAPI)?.instanceId,
  getAppInstance: (editorAPI: EditorAPI) =>
    // @ts-expect-error
    parseInstance(getAppData(editorAPI)?.instance),
  // @ts-expect-error
  getInstance: (editorAPI: EditorAPI) => getAppData(editorAPI)?.instance,
  hasBookingsPreInstallationOffer: (editorAPI: EditorAPI) =>
    stateManagement.introFunnel.selectors.hasAppIntent(
      editorAPI.store.getState(),
    ),
  isAppInstalled: (editorAPI: EditorAPI) =>
    editorAPI.dsRead.tpa.app.isInstalled(WIX_BOOKINGS_APP_DEF_ID),
  isSitePublished: (editorAPI: EditorAPI) =>
    editorAPI.dsRead.generalInfo.isSitePublished(),
  getAppData: (editorAPI: EditorAPI) => getAppData(editorAPI),
  isWixBookingsInstalled: (editorAPI: EditorAPI) =>
    editorAPI.dsRead.tpa.app.isInstalled(WIX_BOOKINGS_APP_DEF_ID),
  isWixStoresInstalled: (editorAPI: EditorAPI) =>
    editorAPI.dsRead.tpa.app.isInstalled(WIX_STORE_APP_DEF_ID),
  isAppProvisioned: (editorAPI: EditorAPI) => {
    const isSiteSaved = !editorAPI.dsRead.generalInfo.isFirstSave();
    const isAppProvisionedOnServer = experiment.isOpen('se_appDefIdUpdates')
      ? editorAPI.dsRead.tpaV2.app.isAppProvisionedOnServer(
          WIX_BOOKINGS_APP_DEF_ID,
        )
      : editorAPI.dsRead.tpa.app.isAppProvisionedOnServer(
          // @ts-expect-error
          WIX_BOOKINGS_APP_DEF_ID,
        );
    return (
      isSiteSaved &&
      isAppProvisionedOnServer &&
      // @ts-expect-error
      !getAppData(editorAPI)?.demoMode
    );
  },
  isTemplate: (editorAPI: EditorAPI) =>
    editorAPI.dsRead.generalInfo.isFirstSave(),
  isInPreInstallationFunnel: (editorAPI: EditorAPI) => {
    const introFunnelAppIntents =
      stateManagement.introFunnel.selectors.getAppIntents(
        editorAPI.store.getState(),
      );
    return introFunnelAppIntents.find(
      (intent) =>
        intent === stateManagement.introFunnel.IntroFunnelIntents.BOOKINGS,
    );
  },
};

const miscActions = {
  hasPremium: () => tpa.superApps.isPremium(WIX_BOOKINGS_APP_DEF_ID),
  getAddElementCategoryId: () => addPanelDataConsts.CATEGORIES_ID.BOOKINGS,
};

// eslint-disable-next-line you-dont-need-lodash-underscore/assign
export default _.assign(
  miscActions,
  editorApiReliantMethods,
  featureToggles,
  superAppData,
  params,
);
